import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Clock, BarChart3, X, Globe, Building2, Users, ChevronRight, Bot } from 'lucide-react';
import { LoadingSpinner, MultiSelect } from '../../../../common';
import { AdminPageHeader, AdminEmptyState, WorkspaceFilter, Pagination } from '../../../ui';
import * as dataSourceService from '../../../../../services/datasourceService';
import * as workspaceService from '../../../../../services/workspaceService';
import { DataSourceWithAgents, DataSourceCreate } from '../../../../../types/dataSource';
import { WorkspaceGroup } from '../../../../../types/workspace';
import { PaginationMetadata } from '../../../../../types/user';
import { useWorkspace } from '../../../../../contexts/WorkspaceContext';
import { useAuth } from '../../../../../contexts/AuthContext';

// DataSource Row Component
interface DataSourceRowProps {
  dataSource: DataSourceWithAgents;
  onClick: () => void;
}

const DataSourceRow: React.FC<DataSourceRowProps> = React.memo(({ dataSource, onClick }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get visibility icon and label
  const getVisibilityInfo = () => {
    if (dataSource.visibility_level === 'global') {
      return { icon: Globe, label: 'Global', color: 'text-purple-400', bgColor: 'bg-purple-500/20' };
    } else if (dataSource.visibility_level === 'group') {
      return { icon: Users, label: 'Grupo', color: 'text-green-400', bgColor: 'bg-green-500/20' };
    } else {
      return { icon: Building2, label: 'Workspace', color: 'text-blue-400', bgColor: 'bg-blue-500/20' };
    }
  };

  const visibilityInfo = getVisibilityInfo();
  const VisibilityIcon = visibilityInfo.icon;

  return (
    <tr
      onClick={onClick}
      className="
        border-b border-[#333333]
        hover:bg-[#2a2a2a] cursor-pointer
        transition-colors
      "
    >
      {/* Nome */}
      <td className="px-4 py-4">
        <div className="flex flex-col">
          <span className="text-white font-medium">{dataSource.name}</span>
          <span className="text-[#888888] text-sm line-clamp-1 mt-0.5">
            {dataSource.description || 'Sem descrição'}
          </span>
        </div>
      </td>

      {/* Visibilidade */}
      <td className="px-4 py-4">
        <span
          className={`
            inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full
            ${visibilityInfo.bgColor} ${visibilityInfo.color}
          `}
        >
          <VisibilityIcon className="w-3.5 h-3.5" />
          {visibilityInfo.label}
        </span>
      </td>

      {/* Tipo RAG */}
      <td className="px-4 py-4">
        <span className="text-sm text-[#cccccc] capitalize">
          {dataSource.rag_type || 'Relational'}
        </span>
      </td>

      {/* Agentes */}
      <td className="px-4 py-4">
        <div className="flex items-center gap-1.5 text-sm text-[#888888]">
          <Bot className="w-3.5 h-3.5" />
          {dataSource.agents?.length || 0}
        </div>
      </td>

      {/* Criado Por */}
      <td className="px-4 py-4">
        {dataSource.created_by_user_name ? (
          <div className="flex flex-col">
            <span className="text-sm text-white">{dataSource.created_by_user_name}</span>
            {dataSource.created_by_user_email && (
              <span className="text-xs text-[#888888]">{dataSource.created_by_user_email}</span>
            )}
          </div>
        ) : (
          <span className="text-xs text-[#666666]">—</span>
        )}
      </td>

      {/* Data de Criação */}
      <td className="px-4 py-4 text-sm text-[#888888] whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          {formatDate(dataSource.created_at)}
        </div>
      </td>

      {/* Data de Atualização */}
      <td className="px-4 py-4 text-sm text-[#888888] whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5" />
          {formatDate(dataSource.updated_at)}
        </div>
      </td>

      {/* Ação */}
      <td className="px-4 py-4">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="p-2 text-[#F4CC04] hover:bg-[#F4CC04]/10 rounded-lg transition-colors"
          title="Ver detalhes"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
});

// Create Modal Component
interface CreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  data: DataSourceCreate;
  onChange: (data: DataSourceCreate) => void;
  isCreating: boolean;
  isSuperAdmin: boolean;
  groups: (WorkspaceGroup & { workspace_name?: string })[];
  workspaces: { id: number; name: string }[];
}

const CreateModal: React.FC<CreateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  data,
  onChange,
  isCreating,
  isSuperAdmin,
  groups,
  workspaces,
}) => {
  if (!isOpen) return null;

  const visibilityLevel = data.visibility_level || 'workspace';

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={onClose}
      />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl p-6 z-50">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Criar Data Source</h2>
          <button
            onClick={onClose}
            className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-[#cccccc] mb-2">
              Nome *
            </label>
            <input
              type="text"
              placeholder="Nome do data source"
              value={data.name}
              onChange={(e) => onChange({ ...data, name: e.target.value })}
              required
              className="
                w-full px-4 py-3
                bg-[#1a1a1a] border border-[#333333]
                text-white placeholder-[#888888]
                rounded-lg
                focus:outline-none focus:border-[#F4CC04]
              "
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#cccccc] mb-2">
              Descrição
            </label>
            <textarea
              placeholder="Descrição do data source"
              value={data.description || ''}
              onChange={(e) => onChange({ ...data, description: e.target.value })}
              rows={3}
              className="
                w-full px-4 py-3
                bg-[#1a1a1a] border border-[#333333]
                text-white placeholder-[#888888]
                rounded-lg resize-none
                focus:outline-none focus:border-[#F4CC04]
              "
            />
          </div>

          {/* Visibility Level Selector */}
          <div>
            <label className="block text-sm font-medium text-[#cccccc] mb-2">
              Nível de Visibilidade
            </label>
            <select
              value={visibilityLevel}
              onChange={(e) => {
                const newLevel = e.target.value as 'workspace' | 'group' | 'global';
                onChange({
                  ...data,
                  visibility_level: newLevel,
                  workspace_ids: (newLevel === 'workspace' || newLevel === 'group') ? (data.workspace_ids || []) : [],
                  group_ids: newLevel === 'group' ? (data.group_ids || []) : [],
                  created_by_group_id: newLevel !== 'group' ? null : data.created_by_group_id
                });
              }}
              className="
                w-full px-4 py-3
                bg-[#1a1a1a] border border-[#333333]
                text-white
                rounded-lg
                focus:outline-none focus:border-[#F4CC04]
              "
            >
              <option value="workspace">Workspace (Visível em workspaces selecionados)</option>
              <option value="group">Grupo (Compartilhamento granular por workspaces e grupos)</option>
              {isSuperAdmin && (
                <option value="global">Global (Visível em todos os workspaces)</option>
              )}
            </select>
            <p className="mt-1 text-xs text-[#888888]">
              {visibilityLevel === 'global' && 'Esta fonte de dados será visível em todos os workspaces do sistema'}
              {visibilityLevel === 'workspace' && 'Selecione os workspaces onde esta fonte de dados estará disponível'}
              {visibilityLevel === 'group' && 'Selecione primeiro os workspaces, depois os grupos específicos que poderão ver esta fonte de dados'}
            </p>
          </div>

          {/* Workspace Multi-Selector (if visibility_level === 'workspace' OR 'group') */}
          {(visibilityLevel === 'workspace' || visibilityLevel === 'group') && (
            <MultiSelect
              label="Workspaces"
              options={workspaces.map(w => ({ id: w.id, name: w.name }))}
              selectedIds={data.workspace_ids || []}
              onChange={(ids) => onChange({ ...data, workspace_ids: ids })}
              placeholder="Selecione os workspaces..."
              required
            />
          )}

          {/* Group Multi-Selector (if visibility_level === 'group') */}
          {visibilityLevel === 'group' && (
            <MultiSelect
              label="Grupos"
              options={groups
                .filter((g): g is WorkspaceGroup & { workspace_name?: string } => !g.deleted)
                .map(g => ({
                  id: g.id,
                  name: g.workspace_name ? `${g.name} - ${g.workspace_name}` : g.name
                }))
              }
              selectedIds={data.group_ids || []}
              onChange={(ids) => onChange({ ...data, group_ids: ids })}
              placeholder="Selecione os grupos..."
              required
            />
          )}

          <button
            type="submit"
            disabled={isCreating || !data.name.trim()}
            className="
              w-full px-4 py-3 mt-2
              bg-[#F4CC04] hover:bg-[#D4B104]
              text-black font-medium
              rounded-lg transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {isCreating ? 'Criando...' : 'Criar Data Source'}
          </button>
        </form>
      </div>
    </>
  );
};

// Workspace data structure for pagination
interface WorkspaceDataSourcesData {
  dataSources: DataSourceWithAgents[];
  currentPage: number;
  paginationData: PaginationMetadata | null;
  isLoading: boolean;
}

// Main Component
export const DataSourceManagement: React.FC = () => {
  const navigate = useNavigate();
  const { workspaces } = useWorkspace();
  const { user } = useAuth();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null | 'global'>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [groups, setGroups] = useState<(WorkspaceGroup & { workspace_name?: string })[]>([]);
  const [allWorkspaces, setAllWorkspaces] = useState<{ id: number; name: string }[]>([]);

  // Per-workspace pagination state
  // Key: unique category key (e.g., "global" for global, "ws-{id}" for workspaces)
  const [workspaceData, setWorkspaceData] = useState<Record<string, WorkspaceDataSourcesData>>({});

  const [newDataSourceData, setNewDataSourceData] = useState<DataSourceCreate>({
    name: '',
    description: '',
    visibility_level: 'workspace',
    created_by_group_id: null,
    workspace_ids: [],
    group_ids: []
  });

  const pageLimit = 20;

  // Refs for cleanup and preventing memory leaks
  const isMountedRef = useRef(true);
  const abortControllersRef = useRef<Record<string, AbortController>>({});

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch all workspaces on mount
  useEffect(() => {
    const fetchAllWorkspaces = async () => {
      try {
        const response = await workspaceService.getUserWorkspaces();
        if (response.success && response.data) {
          setAllWorkspaces(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch workspaces:', err);
      }
    };
    fetchAllWorkspaces();
  }, []);

  // Fetch groups from ALL selected workspaces
  useEffect(() => {
    const fetchGroupsFromAllWorkspaces = async () => {
      const selectedWorkspaceIds = newDataSourceData.workspace_ids;
      if (!selectedWorkspaceIds || selectedWorkspaceIds.length === 0) {
        setGroups([]);
        return;
      }

      try {
        const groupsPromises = selectedWorkspaceIds.map(async (wsId) => {
          const response = await workspaceService.getWorkspaceGroups(wsId);
          if (response.success && response.data) {
            const workspace = allWorkspaces.find(w => w.id === wsId);
            return response.data.map(group => ({
              ...group,
              workspace_name: workspace?.name || `Workspace ${wsId}`
            }));
          }
          return [];
        });

        const allGroupsArrays = await Promise.all(groupsPromises);
        const allGroups = allGroupsArrays.flat();
        setGroups(allGroups);
      } catch (err) {
        console.error('Failed to fetch groups from workspaces:', err);
        setGroups([]);
      }
    };

    fetchGroupsFromAllWorkspaces();
  }, [newDataSourceData.workspace_ids, allWorkspaces]);

  // Handle workspace change
  const handleWorkspaceChange = useCallback((workspaceId: number | null | 'global') => {
    setWorkspaceData({});
    setSelectedWorkspaceId(workspaceId);
  }, []);

  // Fetch data sources for a specific category
  // NOTE: This implementation prevents race conditions and memory leaks by:
  // 1. Aborting previous requests when new ones start (prevents processing stale data)
  // 2. Checking if component is mounted before setState (prevents memory leaks)
  // 3. Cleaning up all controllers on unmount
  // FUTURE IMPROVEMENT: Pass AbortSignal to API service to cancel HTTP requests mid-flight
  const fetchCategoryDataSources = useCallback(
    async (categoryKey: string, page: number = 1, search: string = '') => {
      try {
        // Cancel any existing request for this category
        if (abortControllersRef.current[categoryKey]) {
          abortControllersRef.current[categoryKey].abort();
        }

        // Create new AbortController for this request
        const abortController = new AbortController();
        abortControllersRef.current[categoryKey] = abortController;

        // Update loading state for this category
        if (!isMountedRef.current) return;
        setWorkspaceData(prev => ({
          ...prev,
          [categoryKey]: {
            dataSources: (prev[categoryKey]?.dataSources || []) as DataSourceWithAgents[],
            currentPage: prev[categoryKey]?.currentPage || page,
            paginationData: prev[categoryKey]?.paginationData || null,
            isLoading: true,
          }
        }));

        let workspaceId: number | undefined = undefined;

        // Determine workspace ID from category key
        if (categoryKey === 'global') {
          // Global category - filter by visibility_level on backend
          workspaceId = undefined;
        } else if (categoryKey.startsWith('ws-')) {
          // Extract workspace ID from key format "ws-{id}"
          const wsId = parseInt(categoryKey.substring(3));
          if (!isNaN(wsId)) {
            workspaceId = wsId;
          }
        }

        const response = await dataSourceService.listDataSourcesWithPagination(
          page,
          pageLimit,
          search,
          undefined,
          workspaceId
        );

        // Check if component was unmounted - if so, don't update state
        if (!isMountedRef.current) {
          delete abortControllersRef.current[categoryKey];
          return;
        }

        // Clean up this controller
        delete abortControllersRef.current[categoryKey];

        if (response.success) {
          // Filter by visibility_level if showing global category
          let filteredData: DataSourceWithAgents[] = response.data;
          if (categoryKey === 'global') {
            filteredData = response.data.filter(ds => ds.visibility_level === 'global');
          }

          setWorkspaceData(prev => ({
            ...prev,
            [categoryKey]: {
              dataSources: filteredData,
              currentPage: page,
              paginationData: response.metadata || null,
              isLoading: false,
            }
          }));

        } else {
          setError(response.message || 'Falha ao carregar data sources');
          setWorkspaceData(prev => ({
            ...prev,
            [categoryKey]: {
              dataSources: (prev[categoryKey]?.dataSources || []) as DataSourceWithAgents[],
              currentPage: prev[categoryKey]?.currentPage || page,
              paginationData: prev[categoryKey]?.paginationData || null,
              isLoading: false,
            }
          }));
        }
      } catch (err) {
        // Ignore abort errors but still clean up loading state
        if (err instanceof Error && err.name === 'AbortError') {
          if (isMountedRef.current) {
            setWorkspaceData(prev => ({
              ...prev,
              [categoryKey]: {
                ...prev[categoryKey],
                dataSources: (prev[categoryKey]?.dataSources || []) as DataSourceWithAgents[],
                currentPage: prev[categoryKey]?.currentPage || page,
                paginationData: prev[categoryKey]?.paginationData || null,
                isLoading: false,
              }
            }));
          }
          delete abortControllersRef.current[categoryKey];
          return;
        }

        // Component unmounted, clean up loading state before returning
        if (!isMountedRef.current) {
          delete abortControllersRef.current[categoryKey];
          return;
        }

        setError(err instanceof Error ? err.message : 'Falha ao carregar data sources');
        setWorkspaceData(prev => ({
          ...prev,
          [categoryKey]: {
            dataSources: (prev[categoryKey]?.dataSources || []) as DataSourceWithAgents[],
            currentPage: prev[categoryKey]?.currentPage || page,
            paginationData: prev[categoryKey]?.paginationData || null,
            isLoading: false,
          }
        }));

        // Clean up this controller
        delete abortControllersRef.current[categoryKey];
      }
    },
    [pageLimit]
  );

  // Fetch initial data when selectedWorkspaceId or search changes
  useEffect(() => {
    categories.forEach(cat => {
      fetchCategoryDataSources(cat.key, 1, debouncedSearchQuery);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspaceId, debouncedSearchQuery, workspaces]);

  // Cleanup on unmount - abort all pending requests
  useEffect(() => {
    // Set mounted to true on mount (important for React.StrictMode remounts)
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Abort all pending requests
      Object.values(abortControllersRef.current).forEach(controller => {
        controller.abort();
      });
      abortControllersRef.current = {};
    };
  }, []);

  // Get category info with unique keys and display names
  const categories = useMemo(() => {
    const cats: Array<{ key: string; name: string; workspaceId?: number }> = [];

    if (selectedWorkspaceId === null) {
      // Show all workspaces
      cats.push({ key: 'global', name: 'Data Sources Globais (TD)' });
      workspaces.forEach(w => {
        cats.push({ key: `ws-${w.id}`, name: w.name, workspaceId: w.id });
      });
    } else if (selectedWorkspaceId === 'global') {
      // Show only global
      cats.push({ key: 'global', name: 'Data Sources Globais (TD)' });
    } else {
      // Show specific workspace
      const workspace = workspaces.find(w => w.id === selectedWorkspaceId);
      if (workspace) {
        cats.push({ key: `ws-${workspace.id}`, name: workspace.name, workspaceId: workspace.id });
      }
    }

    return cats;
  }, [selectedWorkspaceId, workspaces]);

  const handleCreateDataSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDataSourceData.name.trim()) return;

    try {
      setIsCreating(true);
      setError(null);
      const response = await dataSourceService.createDataSource(newDataSourceData);
      if (response.success) {
        setNewDataSourceData({
          name: '',
          description: '',
          visibility_level: 'workspace',
          created_by_group_id: null,
          workspace_ids: [],
          group_ids: []
        });
        setShowCreateModal(false);

        // Refresh data for affected categories
        categories.forEach(cat => {
          const data = workspaceData[cat.key];
          if (data) {
            fetchCategoryDataSources(cat.key, data.currentPage, debouncedSearchQuery);
          } else {
            fetchCategoryDataSources(cat.key, 1, debouncedSearchQuery);
          }
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao criar fonte de dados');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDataSourceClick = useCallback((dataSourceId: number) => {
    navigate(`/admin/datasources/${dataSourceId}`);
  }, [navigate]);

  // Handle page change for specific category
  const handleCategoryPageChange = useCallback((category: string, page: number) => {
    fetchCategoryDataSources(category, page, debouncedSearchQuery);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchCategoryDataSources, debouncedSearchQuery]);

  // Check if there are any data sources across all categories
  const hasAnyDataSources = Object.values(workspaceData).some(data => data?.dataSources && data.dataSources.length > 0);

  // Check if any category is currently loading
  const isAnyLoading = Object.values(workspaceData).some(data => data?.isLoading);

  // Show initial loading if still loading AND no data has arrived yet
  const showInitialLoading = isAnyLoading && !hasAnyDataSources;

  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
      <AdminPageHeader
        title="Data Sources"
        subtitle="Gerencie suas conexões de banco de dados"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="
              flex items-center gap-2 px-4 py-2
              bg-[#F4CC04] hover:bg-[#D4B104]
              text-black font-medium
              rounded-lg transition-colors
            "
          >
            <Plus className="w-5 h-5" />
            Novo Data Source
          </button>
        }
      />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <input
          type="text"
          placeholder="Pesquisar data sources..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="
            w-full px-4 py-3 pl-12
            bg-[#1a1a1a] border border-[#333333]
            text-white placeholder-[#888888]
            rounded-lg
            focus:outline-none focus:border-[#F4CC04]
          "
        />
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" />
      </div>

      {/* Workspace Filter */}
      <WorkspaceFilter
        workspaces={workspaces}
        selectedWorkspaceId={selectedWorkspaceId}
        onWorkspaceChange={handleWorkspaceChange}
        className="mb-4"
      />

      {/* Data Sources Table - By Workspace Category */}
      <div className="space-y-8">
        {showInitialLoading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="large" />
          </div>
        ) : hasAnyDataSources ? (
          <>
            {categories.map((cat) => {
              const categoryData = workspaceData[cat.key];

              // Skip if no data yet or still loading for the first time
              if (!categoryData) return null;

              // Skip if no data sources in this category
              if (!categoryData.dataSources || categoryData.dataSources.length === 0) return null;

              return (
                <div key={cat.key} className="space-y-3">
                  {/* Category Header */}
                  <div className="flex items-center gap-3 pb-2 border-b border-[#333333]">
                    <h3 className="text-lg font-semibold text-white">{cat.name}</h3>
                    <span className="text-sm text-[#888888]">
                      ({categoryData.dataSources.length} {categoryData.dataSources.length === 1 ? 'fonte' : 'fontes'})
                    </span>
                  </div>

                  {/* Loading state for this category */}
                  {categoryData.isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner size="medium" />
                    </div>
                  ) : (
                    <>
                      {/* Table for this Category */}
                      <div className="bg-[#1a1a1a] rounded-lg border border-[#333333] overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-[#333333] bg-[#2a2a2a]">
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Nome
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Visibilidade
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Tipo RAG
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Agentes
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Criado Por
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Criado em
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Atualizado em
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Ação
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {categoryData.dataSources.map((ds) => (
                                <DataSourceRow
                                  key={ds.id}
                                  dataSource={ds}
                                  onClick={() => handleDataSourceClick(ds.id)}
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Pagination for this Category */}
                      {categoryData.paginationData && categoryData.paginationData.pages > 1 && (
                        <div className="flex justify-center mt-4">
                          <Pagination
                            currentPage={categoryData.currentPage}
                            totalPages={categoryData.paginationData.pages}
                            onPageChange={(page) => handleCategoryPageChange(cat.key, page)}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <AdminEmptyState
            title={searchQuery ? 'Nenhum data source encontrado' : 'Nenhum Data Source'}
            description={
              searchQuery
                ? 'Tente ajustar os termos de pesquisa'
                : 'Crie seu primeiro data source para começar a gerenciar conexões de banco de dados'
            }
            action={
              !searchQuery ? (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black font-medium rounded-lg transition-colors"
                >
                  Criar Data Source
                </button>
              ) : undefined
            }
          />
        )}
      </div>

      <CreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateDataSource}
        data={newDataSourceData}
        onChange={setNewDataSourceData}
        isCreating={isCreating}
        isSuperAdmin={user?.isSuperAdmin || false}
        groups={groups}
        workspaces={allWorkspaces}
      />
    </div>
  );
};

export default DataSourceManagement;
